/**
 * ONE place that turns a number into text for a member to read.
 *
 * FOUNDER, R2: the whole UI is in the member's chosen language, and "some languages use a different
 * number notation where , is the decimal point and . is the thousands separator". Hand-rolled
 * `toFixed`, `toLocaleString('en-US')` and `${n}%` all print English notation whatever the language,
 * so a German plan read "12.5%" where a German reader expects "12,5 %".
 *
 * THE RULES
 *  - Every formatter takes the number LOCALE as an argument (`opts.locale`). There is no module-level
 *    "current locale": a server renders many members' pages at once, and a shared value would let one
 *    request's language leak into another's. Components read it from the language context
 *    (`useNumberLocale()`); without one the formatter writes English (en-US).
 *  - Notation follows the SITE language. The browser's region refines it only where the region is a real
 *    notation choice inside that language (de-CH/fr-CH/it-CH apostrophe grouping, pt-BR vs pt-PT,
 *    en-GB vs en-US). The English site never picks up another grouping (no en-IN lakh grouping).
 *  - ENGLISH IS BYTE-FOR-BYTE WHAT IT WAS, and what the backend writes (aicio/presentation.py
 *    `format_money`): same rounding (on the binary value, like `toFixed` and Python's `:.1f`), same symbol
 *    placement ("45.0k CHF", "kr45.0k"), same ungrouped percentages ("1234.5%").
 *    numberFormat.legacy.test.ts compares it against the old formatters.
 *  - The CURRENCY always comes from the plan, never from the locale: a German reader of a sterling plan
 *    sees "45,0k £", not euros. The symbol is the one `currencyAffixes` resolves (exact-case: `GBp` is
 *    pence and never wears a pound sign); the locale only decides where it sits and how it is spaced.
 *  - USD ALONE WRITES "$" (founder-approved, 2026-09-21), in every language: "CA$45.0k", "A$", "NZ$",
 *    "HK$", "S$", "MX$", "NT$", and "CN¥" for the yuan (`DISTINCT_CURRENCY_SYMBOLS`).
 *  - Output is CANONICAL: every space Intl writes (U+00A0, U+202F) comes out as U+00A0, and the Swiss
 *    group mark as ’ (U+2019), so a server and a browser with different ICU builds write the same text
 *    and hydration never sees a mismatch.
 *  - Never "-0" or "-0%": a value that rounds to zero is written unsigned.
 */
import { currencyAffixes, exactCurrencyCode, isMajorUnitCode } from './currencyCode'

export const DEFAULT_NUMBER_LOCALE = 'en-US'

// The region a site language reads numbers in when the browser does not name an allowed one.
const DEFAULT_REGION: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-PT',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  nl: 'nl-NL',
  ru: 'ru-RU',
  zh: 'zh-CN',
  'zh-hant': 'zh-TW',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ar: 'ar',
  hi: 'hi-IN',
  id: 'id-ID',
  tr: 'tr-TR',
}

// Regions that are a REAL notation choice within a site language. Any other region keeps the default.
const ALLOWED_REGIONS: Record<string, readonly string[]> = {
  en: ['US', 'GB'],
  de: ['DE', 'CH'],
  fr: ['FR', 'CH'],
  it: ['IT', 'CH'],
  pt: ['PT', 'BR'],
}

function canonicalTag(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null
  } catch {
    return null
  }
}

function baseOf(tag: string): string {
  const lower = tag.toLowerCase()
  if (lower === 'zh-hant' || /^zh[-_](hant|tw|hk|mo)\b/.test(lower)) return 'zh-hant'
  return lower.split(/[-_]/)[0]
}

function regionOf(tag: string): string | undefined {
  try {
    return new Intl.Locale(tag).region
  } catch {
    return undefined
  }
}

// Arabic keeps Western digits: the plan letter, the chat and the engine's own text all print them,
// and one page must not mix two digit sets.
function withNumberingSystem(tag: string): string {
  return baseOf(tag) === 'ar' ? `${tag}-u-nu-latn` : tag
}

/**
 * The number locale for a site language. `language` is the site code ('de', 'zh-Hant') or a full tag
 * ('de-CH'); `browserTags` are the browser's preferred tags, used only for an ALLOWED region of the same
 * language ('de' + ['de-CH', 'en'] -> 'de-CH'; 'de' + ['de-AT'] -> 'de-DE'; 'en' + ['en-IN'] -> 'en-US').
 */
export function resolveNumberLocale(language: string | null | undefined, browserTags: readonly string[] = []): string {
  const raw = (language || '').trim()
  if (!raw) return DEFAULT_NUMBER_LOCALE
  const base = baseOf(raw)
  const allowed = ALLOWED_REGIONS[base] ?? []
  const pick = (region: string | undefined) => {
    if (!region || !allowed.includes(region.toUpperCase())) return null
    return canonicalTag(`${base}-${region.toUpperCase()}`)
  }
  // A full tag names its own region ('de-CH', 'en-GB'): honoured when it is a real choice, or taken as
  // given when the language is not a site language at all ('nl-NL' in tests and exports).
  if (base !== 'zh-hant' && regionOf(raw)) {
    const chosen = pick(regionOf(raw))
    if (chosen) return withNumberingSystem(chosen)
    if (!DEFAULT_REGION[base]) {
      const c = canonicalTag(raw)
      if (c) return withNumberingSystem(c)
    }
  }
  for (const tag of browserTags) {
    if (!tag || baseOf(tag) !== base) continue
    const chosen = pick(regionOf(tag))
    if (chosen) return withNumberingSystem(chosen)
  }
  return withNumberingSystem(DEFAULT_REGION[base] ?? canonicalTag(raw) ?? DEFAULT_NUMBER_LOCALE)
}

/** True for any English number locale (en-US, en-GB): the notation the old formatters wrote. */
export function isEnglishNumberLocale(locale: string | null | undefined): boolean {
  return baseOf(locale || DEFAULT_NUMBER_LOCALE) === 'en'
}

const _fmtCache = new Map<string, Intl.NumberFormat>()
function nf(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let f = _fmtCache.get(key)
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, options)
    } catch {
      f = new Intl.NumberFormat(DEFAULT_NUMBER_LOCALE, options)
    }
    _fmtCache.set(key, f)
  }
  return f
}

/** One spelling for what Intl builds differently across ICU versions (see THE RULES). */
function canon(s: string): string {
  return s.replace(/[  ]/g, ' ').replace(/'/g, '’')
}

type DigitOpts = { locale?: string; maximumFractionDigits?: number; minimumFractionDigits?: number }
type Digits = { minimumFractionDigits: number; maximumFractionDigits: number }
type SignOpt = { signDisplay?: 'auto' | 'exceptZero' }

function digits(o: DigitOpts | undefined, dflt: number): Digits {
  const max = o?.maximumFractionDigits ?? dflt
  const min = Math.min(o?.minimumFractionDigits ?? 0, max)
  return { minimumFractionDigits: min, maximumFractionDigits: max }
}

// `useGrouping: true` means ALWAYS group in current Intl, which would group "1.234" in Spanish where the
// locale's own rule (min2) leaves four digits alone. Only ever pass `false`; otherwise leave Intl's default.
const grouping = (on: boolean): Intl.NumberFormatOptions => (on ? {} : { useGrouping: false })

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/**
 * Round the way the old code did, ON THE BINARY VALUE: `Math.round` for whole numbers, `toFixed` for
 * decimals (Python's `:.1f` rounds the same double the same way). Intl rounds the shortest decimal
 * string instead, which turns 1.15 (really 1.1499…) into 1.2 where the backend writes 1.1.
 * Never returns -0.
 */
function roundLikeLegacy(v: number, d: number): number {
  const r = d <= 0 ? Math.round(v) : Number(v.toFixed(d))
  return r === 0 ? 0 : r
}

/** A plain number in the member's notation: 1234.5 -> "1,234.5" / "1.234,5" / "1 234,5". Default: up to 2 decimals. */
export function formatNumber(value: number | null | undefined, opts?: DigitOpts & SignOpt & { useGrouping?: boolean }): string {
  if (!isNum(value)) return '—'
  const d = digits(opts, 2)
  const v = roundLikeLegacy(value, d.maximumFractionDigits)
  return canon(nf(opts?.locale ?? DEFAULT_NUMBER_LOCALE, {
    ...d, signDisplay: opts?.signDisplay ?? 'auto', ...grouping(opts?.useGrouping ?? true),
  }).format(v))
}

/**
 * A FRACTION as a percentage: 0.025 -> "2.5%" (de: "2,5 %", fr: "2,5 %", tr: "%2,5").
 * Default: up to one decimal, trailing zeros trimmed (0.9 -> "90%"). '—' for a missing value.
 * English is never grouped ("1234.5%"), exactly as the old `${Number((n * 100).toFixed(1))}%`.
 */
export function formatPercent(fraction: number | null | undefined, opts?: DigitOpts & SignOpt): string {
  if (!isNum(fraction)) return '—'
  const d = digits(opts, 1)
  const locale = opts?.locale ?? DEFAULT_NUMBER_LOCALE
  const points = roundLikeLegacy(fraction * 100, d.maximumFractionDigits)
  // `points` has at most `max` decimals; Intl scales points/100 back up in decimal, so it lands on it exactly
  return canon(nf(locale, {
    style: 'percent', ...d, signDisplay: opts?.signDisplay ?? 'auto', ...grouping(!isEnglishNumberLocale(locale)),
  }).format(points / 100))
}

/** A value already in percentage POINTS (0–100) as a percentage: 57 -> "57%". */
export function formatPercentPoints(points: number | null | undefined, opts?: DigitOpts & SignOpt): string {
  if (!isNum(points)) return '—'
  return formatPercent(points / 100, opts)
}

/**
 * `numberText` wrapped in `code`'s symbol the way `locale` writes a POSITIVE amount
 * ("45,0k" + EUR + de-DE -> "45,0k €"). `symbol` overrides the resolved one (e.g. a distinct "C$").
 */
export function wrapCurrency(numberText: string, code: string, locale: string, symbol?: string): string {
  const sym = symbol ?? (currencyAffixes(code).prefix || code)
  let parts: Intl.NumberFormatPart[]
  try {
    parts = nf(locale, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).formatToParts(1)
  } catch {
    return canon(`${sym}${numberText}`)
  }
  return canon(parts.map((p) => (p.type === 'currency' ? sym : p.type === 'integer' ? numberText : p.value)).join(''))
}

const NUMERIC_PART = new Set(['integer', 'group', 'decimal', 'fraction'])

/** Join parts, putting `suffix` (the k/m/B/T scale) right after the last digit. */
function joinWithScale(parts: Intl.NumberFormatPart[], suffix: string): string {
  let last = -1
  parts.forEach((p, i) => { if (NUMERIC_PART.has(p.type)) last = i })
  return parts.map((p, i) => (i === last ? p.value + suffix : p.value)).join('')
}

/**
 * An already-rounded amount with its scale suffix and currency.
 *  - English: the OLD layout exactly — sign, symbol prefix, digits, suffix, then " CODE" when there is no
 *    symbol ("-$1.2m", "45.0k CHF", "kr45.0k", "2.0m GBp").
 *  - Other languages: Intl's layout for the locale with the symbol swapped in; a minor unit writes the
 *    number and then its exact code ("2,0m GBp").
 */
function moneyText(value: number, code: string, locale: string, d: Digits, suffix: string, useGrouping: boolean, symbol?: string): string {
  const v = value === 0 ? 0 : value
  if (isEnglishNumberLocale(locale)) {
    const { prefix, isoSuffix } = symbol ? { prefix: symbol, isoSuffix: '' } : currencyAffixes(code)
    const abs = nf(locale, { ...d, ...grouping(useGrouping) }).format(Math.abs(v))
    return canon(`${v < 0 ? '-' : ''}${prefix}${abs}${suffix}${isoSuffix}`)
  }
  if (isMajorUnitCode(code)) {
    const prefix = symbol ?? currencyAffixes(code).prefix
    try {
      const parts = nf(locale, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', ...d, ...grouping(useGrouping) })
        .formatToParts(v).map((p) => (p.type === 'currency' ? { ...p, value: prefix || code } : p))
      return canon(joinWithScale(parts, suffix))
    } catch { /* fall through to number + code */ }
  }
  return canon(`${joinWithScale(nf(locale, { ...d, ...grouping(useGrouping) }).formatToParts(v), suffix)} ${code}`)
}

// Compact scale suffixes, largest first. [scale, suffix, fixed decimals | null = significant figures]
const SCALES: ReadonlyArray<readonly [number, string, number | null]> = [
  [1e12, 'T', null],
  [1e9, 'B', null],
  [1e6, 'm', 1],
  [1e3, 'k', 1],
]

/**
 * Compact money, the founder's one rule (2026-09-14, shared with the backend's `format_money`):
 *  - < 1,000       -> whole units:               450 -> "$450"          (de: "450 $")
 *  - >= 1,000      -> "k" with one decimal:   45_000 -> "$45.0k"        (de, EUR: "45,0k €")
 *  - >= 1,000,000  -> "m" with one decimal: 1_234_567 -> "$1.2m"        (fr, EUR: "1,2m €")
 *  - >= 1e9 / 1e12 -> B / T by significant figures: 4.56e10 -> "$45.6B"
 * '—' for a missing figure, so it never renders as "$0" or "$NaN".
 */
export function formatMoneyCompact(amount: number | null | undefined, opts?: { currency?: string; locale?: string }): string {
  if (!isNum(amount)) return '—'
  const code = exactCurrencyCode(opts?.currency) || 'USD'
  const locale = opts?.locale ?? DEFAULT_NUMBER_LOCALE
  const sign = amount < 0 ? -1 : 1
  const n = Math.abs(amount)
  const whole = { minimumFractionDigits: 0, maximumFractionDigits: 0 }
  if (n < 1e3) return moneyText(sign * Math.round(n), code, locale, whole, '', true)
  for (const [scale, suffix, fixed] of SCALES) {
    if (n < scale) continue
    const v = n / scale
    if (fixed !== null) {
      // exactly one decimal, kept even when it is .0 — "45.0k" and "1.2m" read as one scale; rounded on
      // the binary value and ungrouped, like the old `toFixed(1)` and Python's f"{v:.1f}"
      return moneyText(sign * Number(v.toFixed(fixed)), code, locale, { minimumFractionDigits: fixed, maximumFractionDigits: fixed }, suffix, false)
    }
    const dg = v >= 100 ? 0 : v >= 10 ? 1 : 2 // 1.23 / 12.3 / 123
    return moneyText(sign * Number(v.toFixed(dg)), code, locale, { minimumFractionDigits: 0, maximumFractionDigits: dg }, suffix, true)
  }
  return moneyText(sign * Math.round(n), code, locale, whole, '', true)
}

/**
 * Full money, no scale suffix: 250000 -> "$250,000" (de, EUR: "250.000 €"; de-CH, CHF: "CHF 250’000").
 * English keeps the old layout: the symbol first, or the ISO code and a space ("CHF 250,000"); a minor
 * unit after the number ("250,000 GBp"). Default: whole units; pass `maximumFractionDigits` for cents.
 * `symbol` overrides the symbol (a document that must tell CA$ from $).
 */
export function formatCurrency(
  amount: number | null | undefined,
  opts?: { currency?: string; locale?: string; maximumFractionDigits?: number; minimumFractionDigits?: number; symbol?: string },
): string {
  if (!isNum(amount)) return '—'
  const code = exactCurrencyCode(opts?.currency) || 'USD'
  const locale = opts?.locale ?? DEFAULT_NUMBER_LOCALE
  const d = digits(opts, 0)
  const v = roundLikeLegacy(amount, d.maximumFractionDigits)
  if (isEnglishNumberLocale(locale)) {
    const abs = nf(locale, d).format(Math.abs(v))
    const sign = v < 0 ? '-' : ''
    if (!isMajorUnitCode(code)) return canon(`${sign}${abs} ${code}`)
    return canon(`${sign}${opts?.symbol || currencyAffixes(code).prefix || `${code} `}${abs}`)
  }
  return moneyText(v, code, locale, d, '', true, isMajorUnitCode(code) ? opts?.symbol : undefined)
}

/**
 * A date in the member's language ("21 Sept 2026" / "21. Sept. 2026" / "21 sept. 2026"), from the same
 * locale as the numbers, so a date never follows the BROWSER's language while the page follows the site's.
 */
export function formatDate(value: Date | string | number, opts?: Intl.DateTimeFormatOptions & { locale?: string }): string {
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  const { locale, ...o } = opts ?? {}
  try {
    return canon(d.toLocaleDateString(locale ?? DEFAULT_NUMBER_LOCALE, o))
  } catch {
    return canon(d.toLocaleDateString(DEFAULT_NUMBER_LOCALE, o))
  }
}

/** A time of day in the member's language. */
export function formatTime(value: Date | string | number, opts?: Intl.DateTimeFormatOptions & { locale?: string }): string {
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  const { locale, ...o } = opts ?? {}
  try {
    return canon(d.toLocaleTimeString(locale ?? DEFAULT_NUMBER_LOCALE, o))
  } catch {
    return canon(d.toLocaleTimeString(DEFAULT_NUMBER_LOCALE, o))
  }
}
